import { api } from './api';
import type { BrandQuery, BrandQueryAxis, BrandQueryStatus } from '@traaker/shared';

export interface GeneratedQueryProposal {
  text: string;
  textHash: string;
  level?: 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | 'pending';
  zone?: string;
  language?: string;
  /** Source group: authority topic (authority) or use case (referral). */
  topic?: string;
}

export interface QueryExampleRow {
  topic: string;
  narrative: string;
  authority: string;
  referral: string;
}

export const brandQueriesApi = {
  list: (brandId: string, axis?: BrandQueryAxis) => {
    const params = axis ? `?axis=${axis}` : '';
    return api.get<BrandQuery[]>(`/brands/${brandId}/queries${params}`);
  },

  create: (
    brandId: string,
    body: {
      axis: BrandQueryAxis;
      text: string;
      level?: 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | 'pending';
      zone?: string;
      language?: string;
    },
  ) => api.post<BrandQuery>(`/brands/${brandId}/queries`, body),

  update: (
    brandId: string,
    queryId: string,
    body: {
      axis: BrandQueryAxis;
      text?: string;
      zone?: string;
      language?: string;
      status?: BrandQueryStatus;
    },
  ) => api.patch<BrandQuery>(`/brands/${brandId}/queries/${queryId}`, body),

  archive: (brandId: string, axis: BrandQueryAxis, queryId: string) =>
    api.delete<{ ok: true }>(`/brands/${brandId}/queries/${axis}/${queryId}`),

  regenerate: (
    brandId: string,
    axis: BrandQueryAxis,
    opts?: {
      languages?: string[];
      zones?: string[];
      topics?: string[];
      count?: number;
      promptBudget?: number;
    },
  ) =>
    api.post<{
      proposals: GeneratedQueryProposal[];
      geoStrategyHash: string;
      remainingQuota: number;
      maxAllowed: number;
    }>(`/brands/${brandId}/queries/regenerate`, {
      axis,
      ...(opts?.languages ? { languages: opts.languages } : {}),
      ...(opts?.zones ? { zones: opts.zones } : {}),
      ...(opts?.topics ? { topics: opts.topics } : {}),
      ...(typeof opts?.count === 'number' ? { count: opts.count } : {}),
      ...(typeof opts?.promptBudget === 'number' ? { promptBudget: opts.promptBudget } : {}),
    }),

  /**
   * Edit the per-axis source groups (authority topics → keywords, referral use
   * cases → scenarios). Server saves then auto re-validates Brand + GEO.
   */
  updateTopics: (brandId: string, axis: BrandQueryAxis, items: string[]) =>
    api.patch<{ items: string[]; field: 'keywords' | 'scenarios' }>(
      `/brands/${brandId}/queries/${axis}/topics`,
      { items },
    ),

  /** Illustrative example queries (narrative/authority/referral) per authority topic. */
  examples: (brandId: string, topics: string[]) =>
    api.post<{ examples: QueryExampleRow[] }>(`/brands/${brandId}/queries/examples`, { topics }),

  applyRegen: (
    brandId: string,
    body: {
      axis: BrandQueryAxis;
      geoStrategyHash?: string;
      items: Array<{ text: string; level?: string; zone?: string; language?: string; topic?: string }>;
    },
  ) =>
    api.post<{
      created: BrandQuery[];
      errors: Array<{ text: string; error: string }>;
    }>(`/brands/${brandId}/queries/regenerate/apply`, body),
};
